/**
 * Was sich die Oberfläche je Person merkt: Filter und Sortierung der Tabellen,
 * der zuletzt gewählte Produktreiter.
 *
 * Gespeichert wird im PC (`/api/auth/me/einstellungen`), nicht im Browser —
 * sonst gälte der Stand nur an einem Rechner, und zwei Personen am selben
 * Rechner sähen die Filter der jeweils anderen.
 *
 * Die Kinder werden erst gezeichnet, wenn die Einstellungen da sind. Sonst
 * stünde jede Tabelle für einen Moment ungefiltert da und spränge dann um —
 * und ein Schreibvorgang aus diesem Zwischenzustand würde den gespeicherten
 * Stand überschreiben.
 */
import React, { createContext, useCallback, useContext, useEffect, useRef, useState } from 'react';
import { auth } from '../api';
import { useAuth } from './AuthContext';

type Werte = Record<string, unknown>;

interface EinstellungenWert {
  werte: Werte;
  setzen: (schluessel: string, wert: unknown) => void;
}

/** Ohne Provider (Login-Seite, Tests): nichts merken, nichts laden. */
const EinstellungenContext = createContext<EinstellungenWert>({ werte: {}, setzen: () => {} });

const SCHREIB_VERZOEGERUNG_MS = 600;

export function EinstellungenProvider({ children }: { children: React.ReactNode }) {
  const { user } = useAuth();
  const [werte, setWerte] = useState<Werte>({});
  const [geladenFuer, setGeladenFuer] = useState<string | null>(null);
  const offen = useRef<Werte>({});
  const zeitgeber = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    if (!user) {
      setWerte({});
      setGeladenFuer(null);
      return;
    }
    let abgebrochen = false;
    auth
      .einstellungen()
      .then((w) => { if (!abgebrochen) setWerte(w ?? {}); })
      // Ohne gespeicherte Einstellungen geht es auch — nur eben ungefiltert.
      .catch(() => { if (!abgebrochen) setWerte({}); })
      .finally(() => { if (!abgebrochen) setGeladenFuer(user.id); });
    return () => { abgebrochen = true; };
  }, [user]);

  const wegschreiben = useCallback(() => {
    const aenderung = offen.current;
    offen.current = {};
    zeitgeber.current = null;
    if (Object.keys(aenderung).length) auth.einstellungenSetzen(aenderung).catch(() => {});
  }, []);

  // Beim Verlassen der Seite nichts verlieren, was noch in der Warteschlange steht.
  useEffect(() => {
    const beimSchliessen = () => { if (zeitgeber.current) { clearTimeout(zeitgeber.current); wegschreiben(); } };
    window.addEventListener('beforeunload', beimSchliessen);
    return () => window.removeEventListener('beforeunload', beimSchliessen);
  }, [wegschreiben]);

  const setzen = useCallback((schluessel: string, wert: unknown) => {
    setWerte((alt) => {
      if (JSON.stringify(alt[schluessel] ?? null) === JSON.stringify(wert ?? null)) return alt;
      const neu = { ...alt };
      if (wert === null || wert === undefined) delete neu[schluessel];
      else neu[schluessel] = wert;
      offen.current[schluessel] = wert ?? null;
      if (zeitgeber.current) clearTimeout(zeitgeber.current);
      zeitgeber.current = setTimeout(wegschreiben, SCHREIB_VERZOEGERUNG_MS);
      return neu;
    });
  }, [wegschreiben]);

  if (user && geladenFuer !== user.id) return null;

  return <EinstellungenContext.Provider value={{ werte, setzen }}>{children}</EinstellungenContext.Provider>;
}

/**
 * Ein gemerkter Wert. Ist `schluessel` leer, wird nichts gemerkt — dann
 * verhält sich der Haken wie ein gewöhnliches useState.
 * Entspricht der Wert der Vorgabe, wird der Schlüssel entfernt statt gespeichert.
 */
export function useEinstellung<T>(schluessel: string | undefined, vorgabe: T): [T, (wert: T) => void] {
  const { werte, setzen } = useContext(EinstellungenContext);
  const [lokal, setLokal] = useState<T>(vorgabe);
  if (!schluessel) return [lokal, setLokal];
  const wert = (schluessel in werte ? (werte[schluessel] as T) : vorgabe);
  const setWert = (neu: T) => setzen(schluessel, JSON.stringify(neu) === JSON.stringify(vorgabe) ? null : neu);
  return [wert, setWert];
}
